import React, { useEffect, useState } from 'react';
import './asset.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { Grid } from '@material-ui/core';
import AgGridContainer from '../../../componant/gridContainer/gridContainer';
import { getAllAssets, getAllEmployee, createAsset } from '../../../api-services/employee-services';
import { AssetModel } from '../../../model/asset-model/asset.model';
import { EmployeeModel } from '../../../model/employee-model/employee.model';
import { SnackbarService } from '../../../services/snackBehaviorSubject/snackBehaviorSubject';
import ButtonWrapper from '../../../widgets/button/button';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Checkbox,
    List,
    ListItem,
    ListItemButton,
    ListItemAvatar,
    ListItemText,
    Avatar,
    CircularProgress,
    Box,
    Typography
} from '@mui/material';

const AdminAsset = () => {
    const snackbarService = SnackbarService();
    const [rowData, setRowData] = useState<AssetModel[]>([]);
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [activeStep, setActiveStep] = useState<number>(0);
    const [employees, setEmployees] = useState<EmployeeModel[]>([]);
    const [loadingEmployees, setLoadingEmployees] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
    const [submittingAsset, setSubmittingAsset] = useState<boolean>(false);

    // Form inputs state
    const [assetName, setAssetName] = useState<string>("");
    const [assetType, setAssetType] = useState<string>("");
    const [brand, setBrand] = useState<string>("");
    const [model, setModel] = useState<string>("");
    const [serialNumber, setSerialNumber] = useState<string>("");
    const [status, setStatus] = useState<string>("");
    const [assetDetails, setAssetDetails] = useState<string>(" ");
    const [assetRequisitionDate, setAssetRequisitionDate] = useState<string>("");
    const [assetReturnDate, setAssetReturnDate] = useState<string>("");

    const handleOpenDialog = () => {
        setOpenDialog(true);
        setLoadingEmployees(true);
        setSelectedEmployeeId(null);
        setSearchTerm('');
        setActiveStep(0);

        // Reset to requested default values
        setAssetName("");
        setAssetType("");
        setBrand("");
        setModel("");
        setSerialNumber("");
        setStatus("");
        setAssetDetails("");
        setAssetRequisitionDate("");
        setAssetReturnDate("");

        getAllEmployee().then((response: any) => {
            if (response && response.data && response.data.success) {
                setEmployees(response.data.payload || []);
            } else {
                setEmployees([]);
            }
            setLoadingEmployees(false);
        }).catch((err: any) => {
            console.error("Error fetching employees: ", err);
            snackbarService.next({ data: { open: true, severity: 'error', message: 'Failed to fetch employees list' } });
            setEmployees([]);
            setLoadingEmployees(false);
        });
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedEmployeeId(null);
        setSearchTerm('');
        setActiveStep(0);
    };

    const handleSelectEmployee = (empId: string) => {
        setSelectedEmployeeId((prevSelected) => (prevSelected === empId ? null : empId));
    };

    const handleAssignAsset = () => {
        if (!selectedEmployeeId) {
            snackbarService.next({ data: { open: true, severity: 'warning', message: 'Please select an employee first' } });
            return;
        }

        setSubmittingAsset(true);

        const assetPayload = {
            assetName,
            assetType,
            brand,
            model,
            serialNumber,
            status,
            assetDetails,
            assetRequisitionDate,
            assetReturnDate
        };

        createAsset(selectedEmployeeId, assetPayload).then((response: any) => {
            if (response && response.data && response.data.success) {
                snackbarService.next({ data: { open: true, severity: 'success', message: response.data.message || 'Asset assigned successfully' } });
                handleCloseDialog();
                fetchData();
            } else {
                const errMsg = response && response.data && response.data.message ? response.data.message : 'Failed to assign asset';
                snackbarService.next({ data: { open: true, severity: 'error', message: errMsg } });
            }
            setSubmittingAsset(false);
        }).catch((err: any) => {
            console.error("Error assigning asset: ", err);
            snackbarService.next({ data: { open: true, severity: 'error', message: 'An error occurred while assigning the asset' } });
            setSubmittingAsset(false);
        });
    };

    const isFormValid = () => {
        return (
            assetName.trim() !== '' &&
            assetType.trim() !== '' &&
            brand.trim() !== '' &&
            model.trim() !== '' &&
            serialNumber.trim() !== '' &&
            status.trim() !== '' &&
            assetRequisitionDate.trim() !== '' &&
            assetReturnDate.trim() !== ''
        );
    };

    const filteredEmployees = employees.filter((emp) => {
        const term = searchTerm.toLowerCase();
        return (
            (emp.name && emp.name.toLowerCase().includes(term)) ||
            (emp.employee_id && emp.employee_id.toLowerCase().includes(term))
        );
    });

    const getInitials = (name: string) => {
        if (!name) return 'EE';
        const parts = name.split(' ');
        return parts.map((p) => p[0]).join('').slice(0, 2).toUpperCase();
    };

    const fetchData = () => {
        getAllAssets().then((response: any) => {
            if (response && response.data) {
                if (response.data.success && response.data.payload) {
                    setRowData(response.data.payload);
                } else if (Array.isArray(response.data)) {
                    setRowData(response.data);
                } else if (response.data.payload) {
                    setRowData(response.data.payload);
                } else {
                    setRowData([]);
                }
            } else {
                setRowData([]);
            }
        }).catch((err: any) => {
            console.error("Error fetching assets: ", err);
            snackbarService.next({ data: { open: true, severity: 'error', message: 'Failed to fetch asset data' } });
            setRowData([]);
        });
    };

    useEffect(() => {
        fetchData();
    }, []);

    const [columnDefs] = useState<any[]>([
        {
            headerName: "ID",
            field: "id",
            resizable: true,
            filter: true,
            flex: 0.5
        },
        {
            headerName: "Asset Name",
            field: "assetName",
            resizable: true,
            filter: true,
            flex: 1
        },
        {
            headerName: "Asset Type",
            field: "assetType",
            resizable: true,
            filter: true,
            flex: 1
        },
        {
            headerName: "Brand",
            field: "brand",
            resizable: true,
            filter: true,
            flex: 1
        },
        {
            headerName: "Model",
            field: "model",
            resizable: true,
            filter: true,
            flex: 1
        },
        {
            headerName: "Serial Number",
            field: "serialNumber",
            resizable: true,
            filter: true,
            flex: 1
        },
        {
            headerName: "Status",
            field: "status",
            resizable: true,
            filter: true,
            flex: 1
        },
        {
            headerName: "Employee ID",
            field: "employeeId",
            resizable: true,
            filter: true,
            flex: 1,
            cellRenderer: (params: any) => {
                return params.value ? params.value : "N/A";
            }
        },
        {
            headerName: "Employee Name",
            field: "employeeName",
            resizable: true,
            filter: true,
            flex: 1.5,
            cellRenderer: (params: any) => {
                return params.value ? params.value : "N/A";
            }
        },
        {
            headerName: "Requisition Date",
            field: "assetRequisitionDate",
            resizable: true,
            filter: true,
            flex: 1,
            cellRenderer: (params: any) => {
                return params.value ? params.value : "N/A";
            }
        },
        {
            headerName: "Return Date",
            field: "assetReturnDate",
            resizable: true,
            filter: true,
            flex: 1,
            cellRenderer: (params: any) => {
                return params.value ? params.value : "N/A";
            }
        }
    ]);

    return (
        <>
            <div className='main'>
                <div className='main-container'>
                    <div className='heading heading-center-space-between height-40'>
                        <Grid container>
                            <Grid item xs={11} className='flex-start-center'>
                                <h2>Asset List</h2>
                            </Grid>
                            <Grid item xs={1} className='flex-end-center'>
                                <ButtonWrapper buttonType='circle' className='circular-add-button' onClick={handleOpenDialog} tooltipTitle='Assign Asset' tooltipPlacement='left' />
                            </Grid>
                        </Grid>
                    </div>
                    <div className='sub-container cal-40'>
                        <Grid item style={{ height: '100%' }}>
                            <div className='asset-container'>
                                <AgGridContainer rowData={rowData} columnDefs={columnDefs} />
                            </div>
                        </Grid>
                    </div>
                </div>
            </div>
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    style: {
                        borderRadius: '12px',
                        padding: '8px'
                    }
                }}
            >
                <DialogTitle style={{ fontWeight: 600, fontSize: '1.25rem', color: 'var(--primaryColor)' }}>
                    {activeStep === 0 ? "Assign Asset - Select Employee (Step 1)" : "Assign Asset - Asset Details (Step 2)"}
                </DialogTitle>
                <DialogContent dividers>
                    {activeStep === 0 ? (
                        <>
                            <Box mb={2}>
                                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '12px' }}>
                                    Select an employee from the list below to assign an asset to them.
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    label="Search Employee by Name or ID"
                                    size="small"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Enter name or ID..."
                                />
                            </Box>

                            {loadingEmployees ? (
                                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                    <CircularProgress size={36} style={{ color: 'var(--primaryColor)' }} />
                                </Box>
                            ) : (
                                <List style={{ maxHeight: '300px', overflowY: 'auto', padding: 0 }}>
                                    {filteredEmployees.length > 0 ? (
                                        filteredEmployees.map((emp) => {
                                            const isSelected = selectedEmployeeId === emp.employee_id;
                                            return (
                                                <ListItem
                                                    key={emp.employee_id}
                                                    disablePadding
                                                    secondaryAction={
                                                        <Checkbox
                                                            edge="end"
                                                            checked={isSelected}
                                                            onChange={() => handleSelectEmployee(emp.employee_id)}
                                                            style={{ color: 'var(--primaryColor)' }}
                                                        />
                                                    }
                                                    style={{
                                                        borderRadius: '8px',
                                                        marginBottom: '6px',
                                                        backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                                                        transition: 'background-color 0.2s'
                                                    }}
                                                >
                                                    <ListItemButton
                                                        onClick={() => handleSelectEmployee(emp.employee_id)}
                                                        style={{ borderRadius: '8px' }}
                                                    >
                                                        <ListItemAvatar>
                                                            <Avatar style={{ backgroundColor: isSelected ? 'var(--primaryColor)' : '#78909c', fontSize: '14px', fontWeight: 600 }}>
                                                                {getInitials(emp.name)}
                                                            </Avatar>
                                                        </ListItemAvatar>
                                                        <ListItemText
                                                            primary={emp.name}
                                                            secondary={`ID: ${emp.employee_id}`}
                                                            primaryTypographyProps={{ style: { fontWeight: 550 } }}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            );
                                        })
                                    ) : (
                                        <Box py={4} textAlign="center">
                                            <Typography color="textSecondary">No employees found</Typography>
                                        </Box>
                                    )}
                                </List>
                            )}
                        </>
                    ) : (
                        <Box
                            component="form"
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: '16px',
                                paddingTop: '8px'
                            }}
                        >
                            <TextField
                                label="Asset Name"
                                size="small"
                                value={assetName}
                                onChange={(e) => setAssetName(e.target.value)}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Asset Type"
                                size="small"
                                value={assetType}
                                onChange={(e) => setAssetType(e.target.value)}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Brand"
                                size="small"
                                value={brand}
                                onChange={(e) => setBrand(e.target.value)}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Model"
                                size="small"
                                value={model}
                                onChange={(e) => setModel(e.target.value)}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Serial Number"
                                size="small"
                                value={serialNumber}
                                onChange={(e) => setSerialNumber(e.target.value)}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Status"
                                size="small"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Requisition Date"
                                type="date"
                                size="small"
                                value={assetRequisitionDate}
                                onChange={(e) => setAssetRequisitionDate(e.target.value)}
                                fullWidth
                                required
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="Return Date"
                                type="date"
                                size="small"
                                value={assetReturnDate}
                                onChange={(e) => setAssetReturnDate(e.target.value)}
                                fullWidth
                                required
                                InputLabelProps={{ shrink: true }}
                            />
                            <Box style={{ gridColumn: 'span 2' }}>
                                <TextField
                                    label="Asset Details"
                                    size="small"
                                    value={assetDetails}
                                    onChange={(e) => setAssetDetails(e.target.value)}
                                    fullWidth
                                    multiline
                                    rows={2}
                                />
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: '16px' }}>
                    {activeStep === 0 ? (
                        <>
                            <Button onClick={handleCloseDialog} color="inherit" disabled={submittingAsset} style={{ fontWeight: 600 }}>
                                Cancel
                            </Button>
                            <Button
                                onClick={() => setActiveStep(1)}
                                variant="contained"
                                disabled={!selectedEmployeeId}
                                style={{
                                    backgroundColor: selectedEmployeeId ? 'var(--primaryColor)' : '#bdbdbd',
                                    color: '#fff',
                                    fontWeight: 600,
                                    minWidth: '120px'
                                }}
                            >
                                Next
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={() => setActiveStep(0)} color="inherit" disabled={submittingAsset} style={{ fontWeight: 600 }}>
                                Back
                            </Button>
                            <Button
                                onClick={handleAssignAsset}
                                variant="contained"
                                disabled={!isFormValid() || submittingAsset}
                                style={{
                                    backgroundColor: isFormValid() && !submittingAsset ? 'var(--primaryColor)' : '#bdbdbd',
                                    color: '#fff',
                                    fontWeight: 600,
                                    minWidth: '120px'
                                }}
                            >
                                {submittingAsset ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AdminAsset;
